import {
  BreadcrumbType,
  Form,
  FormField,
  FormSubmitHandler,
  Separator,
  showNotification,
} from 'platform/components';
import {Heading, Show, VStack} from 'platform/foundation';

import {isNotNil, omit} from 'ramda';

import {useGetCustomerMessageDefinitionQuery} from '@dms/api/core';
import {
  useGetEffectiveValueWithoutReferenceQuery,
  useSetTenantSettingValueMutation,
} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {useNavigate, useRequiredParams} from 'shared/web';

import {AdditionalSettings} from './components/AdditionalSettings';
import {EmailChannelControl} from './components/EmailChannelControl';
import {SmsChannelControl} from './components/SmsChannelControl';
import {SmtpCredentials} from './components/SmtpCredentials';
import {FormValues, formValuesSchema, dynamicUiDefaultValuesKeys} from './types/FormValues';

const SETTINGS_SUFFIX = '-setting';

const breadcrumbs: BreadcrumbType[] = [
  {
    label: i18n.t('page.settings.labels.scheduledMessages'),
    href: settingsRoutes.scheduledMessages,
  },
];

export function ScheduledMessagesDetail() {
  const {id: documentCode} = useRequiredParams();
  const navigate = useNavigate();

  const [updateGeneralSettings] = useSetTenantSettingValueMutation();

  const {data: definition, isLoading: isLoadingDefinition} = useGetCustomerMessageDefinitionQuery({
    id: documentCode,
  });

  const hasSchema = isNotNil(definition?.hasSettings);

  const {data: generalSettings, isLoading: isLoadingGeneralSettings} =
    useGetEffectiveValueWithoutReferenceQuery({
      settingId: `${documentCode}${SETTINGS_SUFFIX}`,
    });

  const handleSubmit: FormSubmitHandler<FormValues> = async (values) => {
    const {
      // these = '' are intended when notification is disabled (handled in schema), so the user can save "empty" values.
      emailContextTemplateBody = '',
      emailContextTemplateSubject = '',
      smsContextTemplateBody = '',
      additionalSettings = {},
      ...submitData
    } = values;

    await updateGeneralSettings({
      settingId: `${documentCode}${SETTINGS_SUFFIX}`,
      valueRequestBody: {
        force: true,
        value: {
          ...submitData,
          ...additionalSettings,
          smsContextTemplate: {
            body: smsContextTemplateBody,
          },
          emailContextTemplate: {
            subject: emailContextTemplateSubject,
            body: emailContextTemplateBody,
          },
          emailContextTemplateSubject,
          emailContextTemplateBody,
          smsContextTemplateBody,
        },
      },
    })
      .unwrap()
      .then(() =>
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'))
      )
      .catch(handleApiError);
  };

  const isLoading = isLoadingGeneralSettings || isLoadingDefinition;
  const dynamicUiDefaultValues = omit(dynamicUiDefaultValuesKeys, generalSettings ?? {});

  return (
    <SettingsTemplate
      header={{
        title: definition?.name ?? i18n.t('page.settings.labels.scheduledMessages'),
        subtitle: definition?.description,
        breadcrumbs,
      }}
      data-testid={testIds.settings.scheduledMessagesDetail('settings-template')}
      isLoading={isLoading}
    >
      <SettingsSection>
        <Form<FormValues>
          experimentalZodSchema={formValuesSchema}
          defaultValues={{...generalSettings, additionalSettings: dynamicUiDefaultValues}}
          onSubmit={handleSubmit}
          shouldWatchForUnsavedChanges
        >
          {(control, formApi, navigateWithoutBlocker) => (
            <VStack spacing={4}>
              <FormField
                control={control}
                type="checkbox"
                name="enabled"
                label={i18n.t('page.settings.labels.automatizationEnabled')}
                helperText={i18n.t('page.settings.labels.automatizationEnabledHelperText')}
              />

              <Separator spacing={0} />
              <Heading size={4}>{i18n.t('page.settings.notifications.channels')}</Heading>

              <SmsChannelControl
                control={control}
                formApi={formApi}
                context={definition?.context}
              />

              <EmailChannelControl
                control={control}
                formApi={formApi}
                context={definition?.context}
              />

              <SmtpCredentials control={control} />

              <Show when={hasSchema}>
                <AdditionalSettings
                  formApi={formApi}
                  control={control}
                  documentCode={documentCode}
                  defaultValues={dynamicUiDefaultValues}
                />
              </Show>

              <SettingsFooter
                actions={[
                  {
                    type: 'button',
                    variant: 'secondary',
                    title: i18n.t('general.actions.discardChanges'),
                    onClick: () => navigateWithoutBlocker(settingsRoutes.scheduledMessages),
                  },
                  {
                    type: 'form-button',
                    control,
                    buttonType: 'submit',
                    title: i18n.t('general.actions.save'),
                  },
                ]}
              />
            </VStack>
          )}
        </Form>
      </SettingsSection>
    </SettingsTemplate>
  );
}
